import type { Variant } from '@amplitude/experiment-js-client';
import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { Fragment } from 'react';

import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { abTestBunja } from '../../../shared/ab-test';

export const AbFlag = () => {
  const { userIdForTrackAtom, deviceIdAtom, prexInstallationId, allVariantsAtom } = useBunja(abFlagBunja);
  const userId = useAtomValue(userIdForTrackAtom);
  const deviceId = useAtomValue(deviceIdAtom);
  const allVariants = useAtomValue(allVariantsAtom);

  return (
    <div className={stack({ flex: 1, align: 'stretch', gap: 16, minH: 0 })}>
      <div className={stack({ gap: 16, flexShrink: 0 })}>
        <h2 className={css({ textStyle: 'Title/XS' })}>A/B Test Flags</h2>
        <dl
          className={css({
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 8,
            rowGap: 2,
            textStyle: 'Text/M/Monospace/Regular',
            '& > dt': {
              py: 2,
              textAlign: 'right',
              color: 'content.default.level-3',
            },
            '& > dd': {
              py: 2,
              px: 8,
              color: 'content.default.level-5',
              bgColor: 'background.default.level-3',
              rounded: 'sm',
            },
          })}
        >
          <dt>user id</dt>
          <dd>{userId || '-'}</dd>
          <dt>device id</dt>
          <dd>{deviceId || '-'}</dd>
          <dt>prex installation id</dt>
          <dd>{prexInstallationId || '-'}</dd>
        </dl>
      </div>
      <dl
        className={css({
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 8,
          rowGap: 2,
          py: 2,
          px: 8,
          rounded: 4,
          textStyle: 'Text/M/Monospace/Regular',
          bgColor: 'background.default.level-0',
          overflowY: 'auto',
          minH: 0,
          '& > dt': {
            color: 'content.default.level-3',
            textAlign: 'right',
          },
          '& > dd': {
            px: 8,
            color: 'content.default.level-5',
            bgColor: 'background.default.level-3',
            rounded: 'sm',
          },
        })}
      >
        {Object.entries(allVariants).map(([key, variant]) => (
          <Fragment key={key}>
            <dt>{key}</dt>
            <dd>{(variant as Variant)?.value || 'off'}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
};

const abFlagBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const abTestBunjaInstance = bunja.use(abTestBunja);
  const { experiment, fetchingAtom } = abTestBunjaInstance;

  const allVariantsAtom = atom(experiment.all());
  function updateAllVariants() {
    store.set(allVariantsAtom, experiment.all());
  }

  bunja.effect(() => {
    updateAllVariants();
    return store.sub(fetchingAtom, updateAllVariants);
  });

  return {
    ...abTestBunjaInstance,
    allVariantsAtom,
  };
});
abFlagBunja.debugLabel = 'abFlagBunja';
